/**
 * "Peek" for the folded rail: rest the pointer on it (or tab into it) and the
 * labelled sidebar floats over the page; leave and it folds away. The old
 * sidebar opened on hover and that is what people reach for; floating keeps
 * the page from moving. Disabled while the sidebar is pinned open.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export const ENTER_DELAY_MS = 150;
export const LEAVE_DELAY_MS = 300;

export function useSidebarPeek({ enabled }) {
  const [peeking, setPeeking] = useState(false);
  const timer = useRef(null);

  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  const close = useCallback(() => {
    clear();
    setPeeking(false);
  }, []);

  useEffect(() => {
    if (!enabled) close();
  }, [enabled, close]);

  useEffect(() => clear, []);

  const onMouseEnter = () => {
    if (!enabled) return;
    clear();
    timer.current = setTimeout(() => setPeeking(true), ENTER_DELAY_MS);
  };

  const onMouseLeave = () => {
    clear();
    timer.current = setTimeout(() => setPeeking(false), LEAVE_DELAY_MS);
  };

  // Keyboard focus only. A mouse press focuses a link before its click lands;
  // peeking then would unfold the rail under the pointer mid-click.
  const onFocus = (event) => {
    if (!enabled || !event.target.matches?.(":focus-visible")) return;
    clear();
    setPeeking(true);
  };

  const onBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    close();
  };

  return { peeking: enabled && peeking, handlers: { onMouseEnter, onMouseLeave, onFocus, onBlur }, close };
}
